import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  closeConnectModal,
  setModalStep,
  setModalProvider,
  appleSetup,
  appleConnect,
  googleBegin,
  saveSelection,
} from '../ducks';
import {
  selectModalStep,
  selectModalProvider,
  selectDiscoveredApple,
  selectDiscoveredGoogle,
  selectPendingApple,
  selectManaging,
  selectModalSubmitting,
  selectModalError,
  selectConnections,
} from '../selectors';
import { ManageSubcalendars } from './ManageSubcalendars';

export function AddConnectionModal(): JSX.Element {
  const dispatch = useDispatch();
  const step = useSelector(selectModalStep);
  const provider = useSelector(selectModalProvider);
  const submitting = useSelector(selectModalSubmitting);
  const error = useSelector(selectModalError);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !submitting) {
        e.preventDefault();
        dispatch(closeConnectModal());
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [dispatch, submitting]);

  return (
    <div
      className="modal modal--calendar-connections"
      role="dialog"
      aria-modal="true"
      aria-labelledby="connect-calendar-title"
      onClick={(e) => {
        if (e.target === e.currentTarget && !submitting) dispatch(closeConnectModal());
      }}
    >
      <div className="modal__panel">
        <div className="modal__header">
          <h3 id="connect-calendar-title" className="modal__title">
            {step === 'subcalendar-picker'
              ? `Pick ${provider === 'google' ? 'Google' : 'Apple'} calendars`
              : step === 'manage-subcalendars'
                ? `Manage ${provider === 'google' ? 'Google' : 'Apple'} calendars`
                : 'Connect a calendar'}
          </h3>
          <button
            type="button"
            className="modal__close"
            aria-label="Close"
            onClick={() => dispatch(closeConnectModal())}
            disabled={submitting}
          >
            ×
          </button>
        </div>

        {error && (
          <div className="modal__error" role="alert">
            {error}
          </div>
        )}

        {step === 'choose-provider' && (
          <div className="modal__body">
            <p>Pick the provider you'd like to import events from:</p>
            <div className="modal__provider-grid">
              <button
                type="button"
                className="modal__provider-btn"
                disabled={submitting}
                onClick={() => {
                  dispatch(setModalProvider('apple'));
                  dispatch(setModalStep('apple-credentials'));
                }}
              >
                Apple iCloud
              </button>
              <button
                type="button"
                className="modal__provider-btn"
                disabled={submitting}
                onClick={() => dispatch(googleBegin())}
              >
                Google Calendar
              </button>
            </div>
          </div>
        )}

        {step === 'apple-credentials' && <AppleCredentialsForm />}

        {step === 'subcalendar-picker' && <SubcalendarPicker />}

        {step === 'manage-subcalendars' && <ManageSubcalendars />}
      </div>
    </div>
  );
}

function AppleCredentialsForm(): JSX.Element {
  const dispatch = useDispatch();
  const submitting = useSelector(selectModalSubmitting);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  return (
    <form
      className="modal__body"
      onSubmit={(e) => {
        e.preventDefault();
        dispatch(appleSetup(email.trim(), password));
      }}
    >
      <p>
        Sign in with your iCloud email and an{' '}
        <a
          href="https://support.apple.com/en-us/102654"
          target="_blank"
          rel="noopener noreferrer"
        >
          app-specific password
        </a>
        .
      </p>
      <label className="modal__field">
        <span>iCloud email</span>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="email"
          required
          disabled={submitting}
        />
      </label>
      <label className="modal__field">
        <span>App-specific password</span>
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="off"
          required
          disabled={submitting}
        />
      </label>
      <div className="modal__actions">
        <button
          type="button"
          className="settings-section__btn"
          onClick={() => dispatch(setModalStep('choose-provider'))}
          disabled={submitting}
        >
          Back
        </button>
        <button
          type="submit"
          className="settings-section__btn settings-section__btn--primary"
          disabled={submitting || !email.trim() || !password}
        >
          {submitting ? 'Checking…' : 'Continue'}
        </button>
      </div>
    </form>
  );
}

function SubcalendarPicker(): JSX.Element {
  const dispatch = useDispatch();
  const provider = useSelector(selectModalProvider);
  const apple = useSelector(selectDiscoveredApple);
  const google = useSelector(selectDiscoveredGoogle);
  const pending = useSelector(selectPendingApple);
  const managingId = useSelector(selectManaging);
  const submitting = useSelector(selectModalSubmitting);
  const connections = useSelector(selectConnections);

  const isApple = provider === 'apple';
  const itemsApple = apple;
  const itemsGoogle = google;

  // Default selection: for Apple new connect, all discovered URIs;
  // for Google new connect, only the primary; for Manage on existing,
  // whatever's already saved.
  const managing = connections.find((c) => c.id === managingId);
  const initialSelected = React.useMemo<string[]>(() => {
    if (managing) {
      return isApple
        ? managing.selected_calendar_uris ?? []
        : managing.selected_google_calendar_ids ?? [];
    }
    if (isApple) {
      return itemsApple.map((c) => c.uri);
    }
    return itemsGoogle.filter((c) => c.primary).map((c) => c.id);
  }, [managing, isApple, itemsApple, itemsGoogle]);

  const [selected, setSelected] = useState<Set<string>>(() => new Set(initialSelected));

  React.useEffect(() => {
    setSelected(new Set(initialSelected));
  }, [initialSelected]);

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const submit = () => {
    const ids = Array.from(selected);
    if (managing) {
      dispatch(saveSelection(managing.id, ids));
      return;
    }
    if (isApple && pending) {
      dispatch(appleConnect({
        email: pending.email,
        appSpecificPassword: pending.appSpecificPassword,
        selectedCalendarUris: ids,
      }));
    } else if (!isApple) {
      // Google: connection row was upserted on callback. Find it.
      const googleConn = connections.find((c) => c.provider === 'google');
      if (googleConn) {
        dispatch(saveSelection(googleConn.id, ids));
      }
    }
  };

  const items: { id: string; label: string; hint?: string }[] = isApple
    ? itemsApple.map((c) => ({ id: c.uri, label: c.name }))
    : itemsGoogle.map((c) => ({
        id: c.id,
        label: c.summary,
        hint: c.primary ? 'primary' : undefined,
      }));

  // Apple "Manage" can't re-discover without the password — we only
  // know the URIs the row already has. Show those as read-only-checked
  // entries with a note explaining why.
  if (isApple && managing && itemsApple.length === 0) {
    const uris = managing.selected_calendar_uris ?? [];
    return (
      <div className="modal__body">
        <p className="modal__hint">
          To change which calendars to sync for this Apple connection,
          disconnect and reconnect — Apple credentials are not stored on
          your device.
        </p>
        <ul className="modal__picker-list">
          {uris.map((uri: string) => (
            <li key={uri}>{uri}</li>
          ))}
        </ul>
        <div className="modal__actions">
          <button
            type="button"
            className="settings-section__btn"
            onClick={() => dispatch(closeConnectModal())}
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="modal__body">
      <p>Pick the calendars you'd like to sync.</p>
      <ul className="modal__picker-list">
        {items.length === 0 ? (
          <li className="modal__picker-empty">No calendars found.</li>
        ) : (
          items.map((it) => (
            <li key={it.id}>
              <label>
                <input
                  type="checkbox"
                  checked={selected.has(it.id)}
                  onChange={() => toggle(it.id)}
                  disabled={submitting}
                />
                <span>{it.label}</span>
                {it.hint && <span className="modal__picker-hint"> ({it.hint})</span>}
              </label>
            </li>
          ))
        )}
      </ul>
      <div className="modal__actions">
        <button
          type="button"
          className="settings-section__btn"
          onClick={() => dispatch(closeConnectModal())}
          disabled={submitting}
        >
          Cancel
        </button>
        <button
          type="button"
          className="settings-section__btn settings-section__btn--primary"
          onClick={submit}
          disabled={submitting || (selected.size === 0 && !managing)}
        >
          {submitting ? 'Saving…' : managing ? 'Save' : 'Connect'}
        </button>
      </div>
    </div>
  );
}

export default AddConnectionModal;
